/**
 * `Readiness` — the Agent Readiness dashboard page.
 *
 * This file is the UI shell: it composes the pure-TS checks from
 * `@sill/types`, loads live data via `useReadinessData`, and renders the
 * hero + goal cards + journey + section cards + fix drawers.
 *
 * Three invariants this file is responsible for:
 *
 *   1. The empty / all-good state is NEVER rendered until the primary
 *      sources have settled. A skeleton holds the layout open while
 *      install / visibility / scan / policy / overview / signals load.
 *
 *   2. The hero counter N = total number of OPEN failing steps (every
 *      failing, rankable row). Dismissed ("Not now") steps are only
 *      skipped for the hero rotation; they still render in their
 *      section with a muted "Snoozed · Undo" affordance.
 *
 *   3. Card-field fixes are applied inline through the typed
 *      `agent-card-profile` write path (If-Match + validation). A
 *      merchant click is the write authority — never auto-apply.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  buildReadinessChecks,
  isStoreSkillId,
  rankReadinessSteps,
  readinessVerdict,
  tallyReadinessSection,
  type ReadinessCheck,
  type ReadinessStep,
} from '@sill/types';

import { useSites, useToast } from '../../app/providers.js';
import { useOptionalHttp } from '../../api/http-context.js';
import { gatingReasonLine } from '../../lib/skill-gating-reason.js';
import { ruleByType } from '../guardrails/catalog.js';
import { ruleConfiguredValueText } from './rule-configured-value.js';
import type { Policy } from '@sill/types';
import {
  APPLY_FIELD_NAMES,
  computeSuggestionContentHash,
  createAgentCardProfileApi,
  type ApplyFieldName,
} from '../../api/agent-card-profile.js';
import { updateSiteCompliance } from '../../api/site-compliance.js';
import { putBusinessSkill, type BusinessSkillId } from '../../api/site-business-skills.js';
import { ActivitySection } from './ActivitySection.js';
import { AgentCardEditorDrawer } from './AgentCardEditorDrawer.js';
import { ChecksSection } from './ChecksSection.js';
import { EditablePair } from './EditablePair.js';
import { FixDrawer } from './FixDrawer.js';
import { GoalCards } from './GoalCards.js';
import { InlineEditValue } from './InlineEditValue.js';
import { JourneyStrip } from './JourneyStrip.js';
import { JourneyDrawer, type JourneyStepKey } from './JourneyDrawer.js';
import { LlmsEditorDrawer } from './LlmsEditorDrawer.js';
import { LoadingSkeleton } from './LoadingSkeleton.js';
import { NextStepCard } from './NextStepCard.js';
import { ReadinessHeader } from './ReadinessHeader.js';
import { SetupSummary } from './SetupSummary.js';
import { WhatsLeftDrawer } from './WhatsLeftDrawer.js';
import { clearDismissedSteps, dismissStep, loadDismissedSteps } from './dismissed-steps.js';
import { sansFont } from './fonts.js';
import { useReadinessData } from './use-readiness-data.js';
import { useC } from '../../lib/theme.js';
import type { SetView } from '../../app/view-router.js';

export interface ReadinessProps {
  readonly setView: SetView;
}

/**
 * Round-3 item 6: when a passing policy row is Guardrails or Signed
 * records, show WHAT is there and link to it. Guardrails: list up to
 * five active rule names from the loaded policy; Signed records: a
 * short "every agent action is signed" line and a link to Mandates.
 *
 * Returning `null` lets ChecksSection fall back to the plain evidence
 * line (unchanged behavior).
 */
function renderPassingExtra(
  check: ReadinessCheck,
  deps: {
    readonly setView: SetView;
    readonly policy: Policy | null;
    readonly rule_count: number | null;
  },
): JSX.Element | null {
  // Round 4 (A): the Guardrails row shows ONLY "N rules active" (which
  // the pure check builder already emits as `evidence`). Rule names +
  // descriptions live in the drawer, reached via the Evidence link.
  // Returning null here lets ChecksSection fall through to the plain
  // evidence string and keep the Evidence link visible.
  if (check.check_id === 'guardrails.active' && check.state === 'pass') {
    void deps;
    return null;
  }
  if (check.check_id === 'audit.signed_records') {
    return <span>Every agent action is signed and chained. Export any time.</span>;
  }
  return null;
}

/**
 * A quiet hero placeholder for the window between the first paint
 * (from cache or from an early-settling source) and the moment the
 * slow sources arrive. It takes the same visual slot as `NextStepCard`
 * but renders nothing claimable — no counts, no "Nothing to fix",
 * no verb buttons. Just a muted "Still checking a few signals…" line.
 */
function HeroChecking(): JSX.Element {
  const C = useC();
  return (
    <section
      aria-busy="true"
      aria-label="Still checking"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 4,
        padding: '28px 32px',
        fontFamily: sansFont,
      }}
    >
      <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.muted, marginBottom: 8 }}>
        Checking
      </div>
      <div style={{ fontSize: 15, color: C.body }}>
        Still checking a few signals. We will show your next step as soon as they land.
      </div>
    </section>
  );
}

function ViewRecordsLink({ onOpen }: { readonly onOpen: () => void }): JSX.Element {
  const C = useC();
  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        background: 'transparent', border: 'none', padding: 0,
        color: C.text, textDecoration: 'underline',
        textDecorationColor: C.dim, textUnderlineOffset: '3px',
        cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
      }}
    >
      View records
    </button>
  );
}

export function Readiness({ setView }: ReadinessProps): JSX.Element {
  const C = useC();
  const toast = useToast();
  const http = useOptionalHttp();
  const { activeSite } = useSites();

  // No active site: show a brief hint and bail BEFORE calling the data
  // hook. `useReadinessData` reads `site.domain` and must not be invoked
  // with `null`.
  if (activeSite === null) {
    return (
      <div
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
        style={{ fontFamily: sansFont, color: C.body }}
      >
        <p>Pick a site to see its readiness.</p>
      </div>
    );
  }
  const site = activeSite; // non-null narrow for the rest of the component

  // ---------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------
  const { data, all_settled, cache_hit, refreshing: data_refreshing, refresh, input } = useReadinessData(site);
  const skills = data.skills;

  // The first "turned on but not live" skill's gating reason — surfaced
  // in the Skills summary line and inside the MCP drawer so the merchant
  // sees the same sentence Settings → Skills shows them.
  const firstStuckReason: string | null = useMemo(() => {
    if (skills === null) return null;
    const stuck = skills.find((s) => s.enabled === true && s.status !== 'live');
    if (stuck === undefined) return null;
    return gatingReasonLine(stuck.skill_id, stuck.gating_reason);
  }, [skills]);

  // ---------------------------------------------------------------
  // Derivation (pure)
  // ---------------------------------------------------------------
  const checks: readonly ReadinessCheck[] = useMemo(
    () => buildReadinessChecks(input),
    [input],
  );
  // 2026-10-02 (§H): the global "Not now" dismiss is removed. Any
  // pre-existing dismissed set is cleared on mount so merchants don't
  // see mysteriously-missing rows. Discrepancy-specific "The card is
  // right" dismissals are still kept — they are a real flag correction.
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => {
    const prev = loadDismissedSteps(site.site_id);
    // Only keep discrepancy-id dismissals (the "card is right" button
    // dismisses the specific scan.discrepancy:* row); clear everything
    // else so step browsing is non-destructive.
    const keep = new Set<string>();
    for (const id of prev) if (id.startsWith('scan.discrepancy:')) keep.add(id);
    if (keep.size !== prev.size) {
      clearDismissedSteps(site.site_id);
      for (const id of keep) dismissStep(site.site_id, id);
    }
    return keep;
  });
  const [refreshing, setRefreshing] = useState(false);
  // "What's left" explainer drawer — opened from the ring and the
  // "N things to fix" line in the header.
  const [whatsLeftOpen, setWhatsLeftOpen] = useState(false);
  // Current step index when the merchant browses the backlog using the
  // next-step arrows. Resets on refresh.
  const [heroStepOffset, setHeroStepOffset] = useState(0);
  // Journey drill-down state — one of 'find' | 'read' | 'understand' |
  // 'act' when a strip node is clicked, null otherwise.
  const [journeyStep, setJourneyStep] = useState<JourneyStepKey | null>(null);

  // All rankable failing steps — the full set. The hero picks from the
  // UNDISMISSED subset, but the section rows (via `dismissed`) still
  // render snoozed rows for every step the merchant pushed to the
  // back of the queue.
  const allSteps: readonly ReadinessStep[] = useMemo(
    () => rankReadinessSteps(checks),
    [checks],
  );
  const openSteps = allSteps.filter((s) => !dismissed.has(s.step_id));
  const safeOffset = openSteps.length === 0 ? 0 : ((heroStepOffset % openSteps.length) + openSteps.length) % openSteps.length;
  const topStep = openSteps[safeOffset] ?? null;
  const understandBlocked = topStep?.step_id === 'crawler.blocked';

  const verdict = useMemo(() => readinessVerdict(checks), [checks]);

  // ---------------------------------------------------------------
  // Drawer state
  // ---------------------------------------------------------------
  const [drawer, setDrawer] = useState<
    | { kind: 'closed' }
    | { kind: 'fix'; step: ReadinessStep | null; check: ReadinessCheck | null }
    | { kind: 'card'; focusFieldId: string | null }
    | { kind: 'llms' }
  >(() => {
    // Deep link: `settings_tab=llms_txt` → open the llms.txt drawer on
    // mount. The URL cleanup effect below strips the param so a manual
    // close doesn't re-reopen on refresh.
    if (typeof window === 'undefined') return { kind: 'closed' };
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('settings_tab') === 'llms_txt') return { kind: 'llms' };
    } catch {
      /* ignore — initial state is closed */
    }
    return { kind: 'closed' };
  });

  // One-shot consumption of the `settings_tab` deep-link param. Mirrors
  // the existing Settings-view cleanup effect so a reload doesn't leave
  // stale state in the URL.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has('settings_tab')) {
        url.searchParams.delete('settings_tab');
        window.history.replaceState({}, '', url.toString());
      }
    } catch {
      /* no-op */
    }
  }, []);

  const openForStep = useCallback((step: ReadinessStep): void => {
    setDrawer({ kind: 'fix', step, check: null });
  }, []);
  const openForCheck = useCallback((check: ReadinessCheck): void => {
    setDrawer({ kind: 'fix', step: null, check });
  }, []);
  const openEvidence = useCallback((check_id: string): void => {
    const found = checks.find((c) => c.check_id === check_id) ?? null;
    if (found !== null) openForCheck(found);
  }, [checks, openForCheck]);
  const closeDrawer = useCallback(() => setDrawer({ kind: 'closed' }), []);

  // ---------------------------------------------------------------
  // Action handlers
  // ---------------------------------------------------------------
  async function checkAgain(): Promise<void> {
    if (refreshing) return; // debounce: ignore clicks while a run is live
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  }

  function dismissDiscrepancyStep(step_id: string): void {
    const next = dismissStep(site.site_id, step_id);
    setDismissed(next);
    toast({ message: 'Flag dismissed. The next scan will recheck.', tone: 'mint' });
  }
  const nextHeroStep = useCallback(() => {
    if (openSteps.length === 0) return;
    setHeroStepOffset((v) => (v + 1) % openSteps.length);
  }, [openSteps.length]);
  const prevHeroStep = useCallback(() => {
    if (openSteps.length === 0) return;
    setHeroStepOffset((v) => (v - 1 + openSteps.length) % openSteps.length);
  }, [openSteps.length]);

  function navigateToSection(section: 'discoverable' | 'policies' | 'activity'): void {
    if (typeof document === 'undefined') return;
    const el = document.getElementById(section);
    if (el !== null) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ---------------------------------------------------------------
  // Inline card-field apply (ADR-0114 typed write path)
  // ---------------------------------------------------------------
  // Match the server's authoritative URL check used by SuggestionsModal
  // / Visibility: trim + lowercase, "https://" required, no credentials.
  function parseFieldFromCheck(check: ReadinessCheck): string | null {
    if (!check.check_id.startsWith('card.field:')) return null;
    return check.check_id.slice('card.field:'.length);
  }

  function suggestedValueFor(field_id: string): string | null {
    const sug = (data.scan?.suggested_card_profile_fields ?? [])
      .find((s) => s.field_id === field_id);
    return sug?.value ?? null;
  }

  async function applyCardSuggestion(check: ReadinessCheck): Promise<void> {
    const field = parseFieldFromCheck(check);
    if (field === null) return;
    const value = suggestedValueFor(field);
    if (value === null) {
      // No raw suggestion — fall back to opening the full editor
      // focused on that field.
      setDrawer({ kind: 'card', focusFieldId: field });
      return;
    }
    // Only writable agent-card profile fields qualify for inline apply
    // through this route; anything else routes through the full editor
    // (compliance uses a different route; `policy.*` uses add_url).
    if (!(APPLY_FIELD_NAMES as readonly string[]).includes(field)) {
      setDrawer({ kind: 'card', focusFieldId: field });
      return;
    }
    if (http === null) {
      setDrawer({ kind: 'card', focusFieldId: field });
      return;
    }
    const cardProfileApi = createAgentCardProfileApi({ http });
    // Resolve the current eTag (route gates on If-Match).
    const etagRes = await cardProfileApi.getEtag(site.site_id);
    const etag =
      etagRes.kind === 'ok' ? etagRes.etag
      : '*';
    // No `source_hint` here — the site-scan surface doesn't carry a
    // `profile_extraction_id` on the SiteScanRow (that id lives on the
    // onboarding profile-extraction result, which the dashboard reads
    // through SuggestionsModal). The server treats the hint as
    // optional forensics; omitting it is a plain merchant-authority
    // apply.
    void computeSuggestionContentHash; // kept imported for symmetry
    const result = await cardProfileApi.apply({
      site_id: site.site_id,
      etag,
      fields: { [field as ApplyFieldName]: value },
    });
    if (result.kind === 'ok') {
      toast({ message: `Saved ${field.replace(/_/g, ' ')}.`, tone: 'mint' });
      // Trigger a background refresh so the row moves to passing.
      void refresh();
      return;
    }
    // Fail-soft: fall through to the full editor; the merchant can
    // review + retry there. The dedicated SuggestionsModal already has
    // the full 412/428 recovery paths — we don't reimplement them here.
    setDrawer({ kind: 'card', focusFieldId: field });
    throw new Error(
      result.kind === 'validation' && result.server_reason !== undefined
        ? `Could not save: ${result.server_reason}.`
        : 'Could not save. Open the editor to retry.',
    );
  }

  async function addPolicyUrl(check: ReadinessCheck, url: string): Promise<void> {
    if (http === null) {
      setDrawer({ kind: 'card', focusFieldId: null });
      return;
    }
    const kind =
      check.step_id === 'policy.tos_missing' ? 'seller_tos_url'
      : check.step_id === 'policy.privacy_missing' ? 'seller_privacy_policy_url'
      : check.step_id === 'policy.returns_missing' ? 'returns_policy'
      : null;
    if (kind === null) return;
    const result = await updateSiteCompliance(http, site.site_id, { [kind]: url });
    if (result.kind === 'ok') {
      toast({ message: 'Saved.', tone: 'mint' });
      void refresh();
      return;
    }
    throw new Error(
      result.kind === 'validation' && result.server_reason !== undefined
        ? `Could not save: ${result.server_reason}.`
        : 'Could not save. Try again.',
    );
  }

  // ---------------------------------------------------------------
  // Narrated section sentences (one-sentence facts, not fluff)
  // ---------------------------------------------------------------
  const discTally = tallyReadinessSection(checks, 'discoverable');
  const polTally = tallyReadinessSection(checks, 'policies');
  // One sentence per goal card, naming what is actually wrong (the first
  // failing check in that section), never a generic phrase.
  // Lower-case the first letter for mid-sentence use, but keep acronyms
  // ("AI crawlers", "MCP server") intact.
  const lowerFirst = (t: string): string =>
    t.length > 1 && t[1] === t[1]!.toUpperCase() && /[A-Z]/.test(t[1]!) ? t : t.charAt(0).toLowerCase() + t.slice(1);
  const firstFail = (group: 'discoverable' | 'policies'): ReadinessCheck | undefined =>
    checks.find((c) => c.group === group && c.state === 'fail' && !c.uncounted);
  const discFail = firstFail('discoverable');
  const discLeft = discTally.counted - discTally.passing;
  const disc_sentence =
    discTally.counted === 0
      ? 'We have not measured any discovery signals yet.'
      : discLeft === 0
        ? 'Agents can find and read you.'
        : discLeft === 1 && discFail !== undefined
          ? `One thing to fix: ${lowerFirst(discFail.headline)}`
          : `${discLeft} things to fix, starting with: ${discFail === undefined ? '' : lowerFirst(discFail.headline)}`;
  const polLeft = polTally.counted - polTally.passing;
  const pol_sentence =
    polTally.counted === 0
      ? 'Every agent action is signed.'
      : polLeft === 0
        ? 'Your policies are published and every agent action is signed.'
        : `Every agent action is signed. ${polLeft} ${polLeft === 1 ? 'policy detail is' : 'policy details are'} missing.`;

  const scriptInstalled = data.install?.browser.state === 'live';
  // Only promise "install is null" once the install network answer
  // has actually come back (or errored). While still in flight — even
  // with a cache paint — leave the header hopeful.
  const install_null_for_header = (data.install_error || (data.install === null && data.install_settled));

  // "Never scanned" is a fail-closed first-run state. It can only
  // appear once every primary source has settled from the network;
  // a cache paint must NEVER flash the first-run copy.
  const neverScanned =
    all_settled
    && data.scan === null
    && data.signals_status === 'ready'
    && data.signals === null
    && !data.scan_error;

  const topIndexForHero = topStep === null
    ? 0
    : openSteps.findIndex((s) => s.step_id === topStep.step_id);
  // Hero counter: "1 of 3" where 3 = number of OPEN failing steps.
  // Dismissed steps are not counted in the hero rotation — they are
  // visible as snoozed rows in their section.
  const hero_total = openSteps.length;

  const dismissedStepIds = dismissed;

  return (
    <div
      className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12"
      style={{ fontFamily: sansFont, color: C.body }}
    >
      <div className="flex flex-col" style={{ gap: 32 }}>
        <ReadinessHeader
          overview={data.overview}
          install_null={install_null_for_header}
          checked_at={data.loaded_at}
          rescan={data.rescan_schedule}
          onRescanChange={(s) => { /* rescan state is read on next poll */ void s; }}
          onRefresh={() => void checkAgain()}
          refreshing={refreshing || data.signals_refreshing}
          site_id={site.site_id}
          verdict={verdict}
          // The verdict is "final" ONLY once every primary source has
          // settled. A cache paint may seed signals, but the counts +
          // verdict depend on install / visibility / scan / policy /
          // overview too — and showing the cached verdict before those
          // land produces the exact partial-count flash the founder
          // reported. Hold the "Checking…" headline until all_settled.
          verdict_loading={!all_settled}
          // "Updating…" chip conditions: a cache paint waiting for
          // network OR any slow source still in flight after a mount.
          updating={!all_settled && cache_hit}
          background_refreshing={data_refreshing}
          // Fix count: NEVER partial. Null until every primary source
          // has settled so the sub-line stays off the screen rather
          // than counting up from 1 to 4 as fetches land.
          fix_count={all_settled ? openSteps.length : null}
          ai_visits={data.overview?.totals.traffic ?? null}
          onViewAgentVisits={() => setView('agents', { agent_kind: 'ai' })}
          signals_refresh_failed={data.signals_refresh_failed}
          signals_loaded_at={data.signals_loaded_at}
          onExplainWhatsLeft={() => setWhatsLeftOpen(true)}
          has_snapshot={data.has_snapshot}
          run_in_progress={data.run_in_progress || refreshing}
        />

        {/* `!all_settled` holds the skeleton — not `loading` — so a
         *   cache paint (or a single fast DB read) can no longer flash
         *   partial counts. Every number below (fix_count, goal-card
         *   fractions, hero "N of M", "N checks passing") depends on the
         *   same `checks` list, which only becomes final when EVERY
         *   primary source has returned. The skeleton stays until then.
         */}
        {!all_settled ? (
          <LoadingSkeleton />
        ) : (
          <>
            {neverScanned ? (
              <section
                aria-label="First-run"
                style={{
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 4,
                  padding: '28px 32px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  fontFamily: sansFont,
                }}
              >
                <h2 style={{ margin: 0, fontFamily: 'Instrument Serif, serif', fontWeight: 400, fontSize: 28, color: C.text }}>
                  Run your first check
                </h2>
                <p style={{ margin: 0, fontSize: 14, color: C.body, lineHeight: 1.6, maxWidth: 540 }}>
                  We will read your site like an agent would and report what we find. Nothing is shared.
                </p>
                <button
                  type="button"
                  onClick={() => void checkAgain()}
                  style={{
                    alignSelf: 'flex-start',
                    fontFamily: sansFont, fontSize: 14, fontWeight: 600,
                    padding: '12px 18px', minHeight: 44,
                    background: C.lime, color: C.ink, border: `1px solid ${C.lime}`, borderRadius: 3, cursor: 'pointer',
                  }}
                >
                  Run first check
                </button>
              </section>
            ) : topStep === null && !all_settled ? (
              <HeroChecking />
            ) : (() => {
              // Build the hero's inline-fix UI, if any:
              //   - missing policy URL  → prefilled URL input + Save
              //   - card.field: with a known value → "Use <value>"
              //   - card.field:return_window_days with a known value → inline days input
              //   - scan.discrepancy:* → secondary "The card is right"
              let inlineFix: React.ReactNode | undefined;
              let primary: { label: string; onClick: () => void } | null = null;
              let onDismiss: (() => void) | null = null;
              if (topStep !== null) {
                const check = checks.find((c) => c.check_id === topStep.check_id) ?? null;
                const isPolicy = topStep.step_id.startsWith('policy.') && topStep.step_id.endsWith('_missing');
                const kindForPolicy =
                  topStep.step_id === 'policy.tos_missing' ? 'seller_tos_url'
                  : topStep.step_id === 'policy.privacy_missing' ? 'seller_privacy_policy_url'
                  : topStep.step_id === 'policy.returns_missing' ? 'returns_policy'
                  : null;
                const policyKindLabel =
                  topStep.step_id === 'policy.tos_missing' ? 'Terms of service URL'
                  : topStep.step_id === 'policy.privacy_missing' ? 'Privacy policy URL'
                  : 'Returns policy URL';
                if (isPolicy && kindForPolicy !== null) {
                  const sugKinds =
                    topStep.step_id === 'policy.tos_missing' ? ['seller_tos_url', 'terms', 'tos']
                    : topStep.step_id === 'policy.privacy_missing' ? ['seller_privacy_policy_url', 'privacy', 'privacy_policy']
                    : ['refund_policy', 'return_policy', 'returns'];
                  const sug = (data.scan?.suggested_policy_urls ?? []).find((u) => sugKinds.includes(u.kind));
                  inlineFix = (
                    <InlineEditValue
                      label={policyKindLabel}
                      value={null}
                      kind="url"
                      site_domain={site.domain}
                      prefill={sug?.url ?? null}
                      onSave={async (next) => {
                        if (http === null) return;
                        const r = await updateSiteCompliance(http, site.site_id, { [kindForPolicy]: next });
                        if (r.kind === 'ok') { toast({ message: 'Saved. Your agent card updates within a few minutes.', tone: 'mint' }); void refresh(); return; }
                        throw new Error(
                          r.kind === 'validation' && r.server_reason !== undefined
                            ? `${r.server_reason}.`
                            : 'Try again.',
                        );
                      }}
                      initiallyEditing
                      onOpenFullEditor={() => setDrawer({ kind: 'card', focusFieldId: null })}
                    />
                  );
                } else if (topStep.step_id.startsWith('card.field:') && check !== null && topStep.action?.verb === 'use_value') {
                  const field = topStep.step_id.slice('card.field:'.length);
                  const sug = (data.scan?.suggested_card_profile_fields ?? []).find((s) => s.field_id === field);
                  const value = sug?.value ?? '';
                  if (field === 'return_window_days' && /^\d+$/.test(value)) {
                    inlineFix = (
                      <InlineEditValue
                        label="Return window"
                        value={null}
                        kind="days"
                        site_domain={site.domain}
                        prefill={value}
                        onSave={async (next) => { await applyCardSuggestion(check); void next; }}
                        initiallyEditing
                        onOpenFullEditor={() => setDrawer({ kind: 'card', focusFieldId: field })}
                      />
                    );
                  } else {
                    primary = { label: topStep.action.label, onClick: () => void applyCardSuggestion(check) };
                  }
                } else if (topStep.action?.verb === 'turn_on') {
                  primary = {
                    label: topStep.action.label,
                    onClick: () => setView('settings', { settings_tab: 'skills', settings_section: 'skills-list' }),
                  };
                } else if (topStep.step_id.startsWith('scan.discrepancy:')) {
                  onDismiss = () => dismissDiscrepancyStep(topStep.step_id);
                }
              }
              return (
                <NextStepCard
                  step={topStep}
                  step_index={Math.max(0, topIndexForHero)}
                  step_total={hero_total}
                  onShowMeHow={() => topStep !== null && openForStep(topStep)}
                  onPrev={prevHeroStep}
                  onNext={nextHeroStep}
                  primary={primary}
                  inline_fix={inlineFix}
                  onDismissDiscrepancy={onDismiss}
                />
              );
            })()}

            <GoalCards
              discoverable={discTally}
              discoverable_sentence={disc_sentence}
              policies={polTally}
              policies_sentence={pol_sentence}
              overview={data.overview}
              scriptInstalled={scriptInstalled}
              onNavigate={navigateToSection}
              has_snapshot={data.has_snapshot}
            />

            <JourneyStrip
              overview={data.overview}
              understandBlocked={understandBlocked}
              serverSideLive={data.install?.server_side.state === 'live'}
              setView={setView}
              onNodeClick={(step) => setJourneyStep(step)}
            />

            <ChecksSection
              id="discoverable"
              group="discoverable"
              title="Discoverable"
              subtitle="Can AI agents find and read you?"
              checks={checks}
              pending_signals={!all_settled}
              promoted_check_id={topStep !== null && topStep.group === 'discoverable' ? topStep.check_id : null}
              dismissedStepIds={dismissedStepIds}
              /* onUndismiss removed §H */
              onAction={(c) => {
                if (c.action?.verb === 'edit_card') { setDrawer({ kind: 'card', focusFieldId: null }); return; }
                if (c.action?.verb === 'edit_llms') { setDrawer({ kind: 'llms' }); return; }
                // Mcp.no_tools and anywhere else "Turn on skills" surfaces:
                // deep-link into Settings → Skills with the list outlined.
                if (c.action?.verb === 'turn_on') {
                  setView('settings', { settings_tab: 'skills', settings_section: 'skills-list' });
                  return;
                }
                openForCheck(c);
              }}
              onEvidence={openEvidence}
              onApplySuggestion={applyCardSuggestion}
              hiddenCheckIds={new Set(['agent_card', 'llms_txt'])}
              extra={(() => {
                const cardCheck = checks.find((c) => c.check_id === 'agent_card');
                const llmsCheck = checks.find((c) => c.check_id === 'llms_txt');
                return (
                  <EditablePair
                    cardHeadline={cardCheck?.headline ?? 'Agent card'}
                    cardEvidence={cardCheck?.evidence ?? 'Needs the Cloudflare Worker or WordPress plugin.'}
                    llmsHeadline={llmsCheck?.headline ?? 'llms.txt'}
                    llmsEvidence={llmsCheck?.evidence ?? 'Needs the Cloudflare Worker or WordPress plugin.'}
                    onEditCard={() => setDrawer({ kind: 'card', focusFieldId: null })}
                    onEditLlms={() => setDrawer({ kind: 'llms' })}
                  />
                );
              })()}
            />

            <ChecksSection
              id="policies"
              group="policies"
              title="Policies & records"
              subtitle="What agents need to trust you, and the proof Sill keeps"
              checks={checks}
              pending_signals={!all_settled}
              promoted_check_id={topStep !== null && topStep.group === 'policies' ? topStep.check_id : null}
              dismissedStepIds={dismissedStepIds}
              /* onUndismiss removed §H */
              onAction={(c) => {
                // Card-field rows that failed to apply inline get an
                // "Edit" fallback — route to the full editor focused
                // on the field.
                const field = parseFieldFromCheck(c);
                if (field !== null) {
                  setDrawer({ kind: 'card', focusFieldId: field });
                  return;
                }
                if (c.action?.verb === 'edit_card') { setDrawer({ kind: 'card', focusFieldId: null }); return; }
                openForCheck(c);
              }}
              onEvidence={openEvidence}
              onApplySuggestion={applyCardSuggestion}
              onAddUrl={addPolicyUrl}
              domain={site.domain}
              suggestedPolicyUrlFor={(step_id) => {
                // The ONE source for scan suggestions. We key on the
                // step id and resolve the matching URL from the latest
                // site-scan payload loaded by `useReadinessData`.
                const urls = data.scan?.suggested_policy_urls ?? [];
                const kinds =
                  step_id === 'policy.privacy_missing' ? ['seller_privacy_policy_url', 'privacy', 'privacy_policy']
                  : step_id === 'policy.tos_missing' ? ['seller_tos_url', 'terms', 'tos']
                  : step_id === 'policy.returns_missing' ? ['refund_policy', 'return_policy', 'returns']
                  : [];
                for (const k of kinds) {
                  const hit = urls.find((u) => u.kind === k);
                  if (hit !== undefined) return hit.url;
                }
                return null;
              }}
              passingExtra={(c) => renderPassingExtra(c, { setView, policy: data.policy, rule_count: data.policy_rule_count })}
              passingAction={(c) => (c.check_id === 'audit.signed_records' ? <ViewRecordsLink onOpen={() => setView('mandates')} /> : null)}
              passingInlineEdit={(c) => {
                // Policy URL rows (tos / privacy / returns) use inline
                // edit. The returns row stores the URL under
                // `returns_policy`; the other two under their own kind.
                const kind: 'seller_tos_url' | 'seller_privacy_policy_url' | 'returns_policy' | null =
                  c.check_id === 'policy.tos' ? 'seller_tos_url'
                  : c.check_id === 'policy.privacy' ? 'seller_privacy_policy_url'
                  : c.check_id === 'policy.returns' ? 'returns_policy'
                  : null;
                if (kind !== null) {
                  const compliance = data.visibility?.site_compliance_values ?? null;
                  const current = c.check_id === 'policy.tos' ? compliance?.seller_tos_url ?? null
                    : c.check_id === 'policy.privacy' ? compliance?.seller_privacy_policy_url ?? null
                    : compliance?.returns_policy ?? null;
                  return (
                    <InlineEditValue
                      label=""
                      value={current}
                      kind="url"
                      site_domain={site.domain}
                      onSave={async (next) => {
                        if (http === null) return;
                        const r = await updateSiteCompliance(http, site.site_id, { [kind]: next });
                        if (r.kind === 'ok') { toast({ message: 'Saved. Your agent card updates within a few minutes.', tone: 'mint' }); void refresh(); return; }
                        throw new Error(
                          r.kind === 'validation' && r.server_reason !== undefined
                            ? `${r.server_reason}.`
                            : 'Try again.',
                        );
                      }}
                      onOpenFullEditor={() => setDrawer({ kind: 'card', focusFieldId: null })}
                      data_testid={`readiness-inline-${c.check_id}`}
                    />
                  );
                }
                // card.field:return_window_days (passing) → inline days edit
                if (c.check_id === 'card.field:return_window_days') {
                  const value = data.visibility?.fields.find((f) => f.id === 'return_window_days')?.value ?? null;
                  return (
                    <InlineEditValue
                      label=""
                      value={value}
                      kind="days"
                      site_domain={site.domain}
                      onSave={async () => { setDrawer({ kind: 'card', focusFieldId: 'return_window_days' }); }}
                      onOpenFullEditor={() => setDrawer({ kind: 'card', focusFieldId: 'return_window_days' })}
                      data_testid="readiness-inline-return-window"
                    />
                  );
                }
                return null;
              }}
            />

            <ActivitySection
              overview={data.overview}
              onNavigate={() => setView('agents', { agent_kind: 'ai' })}
              setView={setView}
            />

            <SetupSummary
              install={data.install}
              skills={skills}
              onInstall={() => setView('settings', { settings_tab: 'install', settings_section: 'how-installed' })}
              onSkills={() => setView('settings', { settings_tab: 'skills', settings_section: 'skills-list' })}
              onProtocols={() => setView('settings', { settings_tab: 'install', settings_section: 'protocol-support' })}
            />
          </>
        )}
      </div>

      <FixDrawer
        open={drawer.kind === 'fix'}
        onClose={closeDrawer}
        step={drawer.kind === 'fix' ? drawer.step : null}
        check={drawer.kind === 'fix' ? drawer.check : null}
        domain={site.domain}
        site_id={site.site_id}
        install={data.install}
        checked_at={data.loaded_at}
        onCheckAgain={() => void checkAgain()}
        onOpenInstall={() => { setDrawer({ kind: 'closed' }); setView('settings', { settings_tab: 'install', settings_section: 'how-installed' }); }}
        onOpenCardEditor={() => setDrawer({ kind: 'card', focusFieldId: null })}
        onOpenLlmsEditor={() => setDrawer({ kind: 'llms' })}
        onOpenSkills={() => { setDrawer({ kind: 'closed' }); setView('settings', { settings_tab: 'skills', settings_section: 'skills-list' }); }}
        onOpenGuardrails={() => { setDrawer({ kind: 'closed' }); setView('guardrails'); }}
        site_key_hash={site.site_key_hash ?? null}
        ai_bots_status={data.signals?.ai_bots_status ?? null}
        ai_access={data.signals?.ai_access ?? null}
        mcp_tools_count={typeof input.live_skill_count === 'number' ? input.live_skill_count : null}
        enabled_skill_count={skills === null ? null : skills.filter((s) => s.enabled === true).length}
        first_stuck_reason={firstStuckReason}
        agent_card_url={`https://${site.domain}/.well-known/agent-card.json`}
        llms_txt_url={`https://${site.domain}/llms.txt`}
        rule_count={data.policy_rule_count}
        active_rules={(data.policy?.rules ?? []).filter((r) => r.enabled).map((r) => {
          const def = ruleByType(r.type);
          return {
            rule_id: r.rule_id,
            display_name: r.display_name ?? def?.name ?? r.type,
            description: r.description ?? def?.description ?? null,
            configured_value: ruleConfiguredValueText(r),
          };
        })}
        card_fields_present={input.visibility?.card_fields_present ?? []}
        card_fields_missing={input.visibility?.card_fields_missing ?? []}
        discrepancies={data.scan?.discrepancy_flags ?? []}
        card_profile_value={(field_id) => {
          const f = data.visibility?.fields.find((v) => v.id === field_id);
          return f?.value ?? null;
        }}
        live_skill_ids={(skills ?? []).filter((s) => s.status === 'live').map((s) => s.skill_id)}
        suggested_skills={
          // Filter out store-only skills on a website — "turn on
          // browse_catalog" is nonsense advice for a SaaS marketing
          // site. Stores see the full suggestion set.
          input.platform_kind === 'website'
            ? (data.scan?.suggested_skills ?? []).filter(
                (s) => !isStoreSkillId(s.skill_id),
              )
            : (data.scan?.suggested_skills ?? [])
        }
        suggested_card_fields={data.scan?.suggested_card_profile_fields ?? []}
        suggested_policy_urls={data.scan?.suggested_policy_urls ?? []}
        // ADR-0222 + ADR-0224 — detected business links (pricing /
        // demo / signup / booking / contact / status / trust) from the
        // homepage probe, filtered to the seven slot kinds.
        detected_business_links={
          ((data.scan?.key_pages ?? []) as readonly { kind: string; url: string }[])
            .filter(
              (
                kp,
              ): kp is {
                kind:
                  | 'pricing'
                  | 'demo'
                  | 'signup'
                  | 'booking'
                  | 'contact'
                  | 'status'
                  | 'trust';
                url: string;
              } =>
                kp.kind === 'pricing'
                || kp.kind === 'demo'
                || kp.kind === 'signup'
                || kp.kind === 'booking'
                || kp.kind === 'contact'
                || kp.kind === 'status'
                || kp.kind === 'trust',
            )
        }
        // ADR-0222 + ADR-0224 — one-click publish for every detected
        // link. The handler walks the slots serially (short list,
        // serial keeps audit-log ordering readable) and surfaces per-
        // slot failures to the toast. Partial success still returns a
        // filled list of failures so the caller can surface them.
        onPublishBusinessLinks={async (links) => {
          if (http === null) return [];
          const failures: { kind: string; reason: string }[] = [];
          for (const link of links) {
            const slot: BusinessSkillId =
              link.kind === 'pricing' ? 'get_pricing'
              : link.kind === 'demo' ? 'request_demo'
              : link.kind === 'signup' ? 'sign_up'
              : link.kind === 'booking' ? 'book_meeting'
              : link.kind === 'contact' ? 'contact_support'
              : link.kind === 'status' ? 'get_status'
              : 'get_trust_center';
            const r = await putBusinessSkill(http, site.site_id, slot, {
              url: link.url,
              enabled: true,
            });
            if (r.kind !== 'ok') {
              failures.push({
                kind: link.kind,
                reason:
                  r.kind === 'bad_request' ? r.reason
                  : r.kind === 'forbidden' ? 'forbidden'
                  : r.kind === 'not_found' ? 'not_found'
                  : r.message,
              });
            }
          }
          if (failures.length === 0) {
            toast({ message: 'Saved. Your agent card updates within a few minutes.', tone: 'mint' });
          } else if (failures.length === links.length) {
            toast({
              message: 'None of the links could be published. Open Settings → Skills to retry.',
              tone: 'coral',
            });
          } else {
            toast({
              message: `${links.length - failures.length} of ${links.length} published. ${failures.length} failed: ${failures.map((f) => f.kind).join(', ')}.`,
              tone: 'amber',
            });
          }
          void refresh();
          return failures;
        }}
        business_name={data.visibility?.fields.find((f) => f.id === 'name')?.value ?? null}
        dismissDiscrepancy={(step_id) => {
          const next = dismissStep(site.site_id, step_id);
          setDismissed(next);
          toast({ message: 'Flag dismissed. The next scan will recheck.', tone: 'mint' });
        }}
        policy={data.policy}
        current_tos_url={data.visibility?.site_compliance_values?.seller_tos_url ?? null}
        current_privacy_url={data.visibility?.site_compliance_values?.seller_privacy_policy_url ?? null}
        current_returns_url={data.visibility?.site_compliance_values?.returns_policy ?? null}
        onInlineSavePolicy={async (kind, value) => {
          if (http === null) return;
          const r = await updateSiteCompliance(http, site.site_id, { [kind]: value });
          if (r.kind === 'ok') { toast({ message: 'Saved. Your agent card updates within a few minutes.', tone: 'mint' }); void refresh(); return; }
          throw new Error(
            r.kind === 'validation' && r.server_reason !== undefined
              ? `${r.server_reason}.`
              : 'Try again.',
          );
        }}
      />
      <AgentCardEditorDrawer
        open={drawer.kind === 'card'}
        onClose={closeDrawer}
        setView={setView}
        focusFieldId={drawer.kind === 'card' ? drawer.focusFieldId : null}
      />
      <LlmsEditorDrawer
        open={drawer.kind === 'llms'}
        onClose={closeDrawer}
        site={site}
      />
      <JourneyDrawer
        open={journeyStep !== null}
        onClose={() => setJourneyStep(null)}
        step={journeyStep}
        overview={data.overview}
        site_domain={site.domain}
        setView={setView}
        onOpenProbes={() => { setJourneyStep(null); setView('security', { security_section: 'paths' }); }}
      />
      <WhatsLeftDrawer
        open={whatsLeftOpen}
        onClose={() => setWhatsLeftOpen(false)}
        checks={checks}
        onFix={(c) => {
          setWhatsLeftOpen(false);
          openForCheck(c);
        }}
      />
    </div>
  );
}
